import {Suspense} from 'react'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {GearIcon} from '@primer/octicons-react'
import {isCopilot} from '@github-ui/assignees/copilot-user'
import {AssigneePicker, type AssigneePickerAssignee} from '@github-ui/item-picker-assignee-tsq'
import {SidebarDisclosureHeader} from './SidebarDisclosureHeader'
import {useAssigneesQuery} from './data/route-queries'
import {useRouteParams} from '@github-ui/react-core/future/use-route-params'
import {pullRequestsOverviewRoute} from '../../routes/overview-route'
import {useModifyAssignees} from '../../mutations/use-modify-assignees'

/**
 * "Manage assignees" header for the Overview sidebar. Anchors the
 * shared assignee picker (`@github-ui/item-picker-assignee-tsq`) to the section
 * header, mirroring the read-only list body below it.
 *
 * Humans-only (v1): Copilot/agentic bots are filtered out of both the
 * pre-selected set and the applied diff. Assigning agents from overview
 * is a follow-up that needs the dedicated agent-authorization flow.
 */
export function AssigneesPicker() {
  return (
    <ErrorBoundary
      boundaryName="AssigneesPicker"
      fallback={<AssigneesHeader disabled aria-label="Assignees couldn't be loaded" />}
    >
      <Suspense fallback={<AssigneesHeader disabled aria-label="Loading assignees" />}>
        <AssigneesPickerContent />
      </Suspense>
    </ErrorBoundary>
  )
}

function AssigneesHeader(props: React.ComponentPropsWithRef<'button'>) {
  return <SidebarDisclosureHeader title="Assignees" trailingIcon={GearIcon} aria-label="Manage assignees" {...props} />
}

function AssigneesPickerContent() {
  const {data: assigneesData} = useAssigneesQuery()
  const {owner, repo, pr_number: prNumber} = useRouteParams(pullRequestsOverviewRoute)
  const number = Number(prNumber)
  const {mutate} = useModifyAssignees()

  // Humans-only (v1): the sidebar picker never manages Copilot/agent assignees.
  const currentHumanAssignees = (assigneesData.assignees ?? []).filter(a => !isCopilot(a.displayLogin))
  const selectedAssignees: AssigneePickerAssignee[] = currentHumanAssignees.map(a => ({
    id: a.nodeId,
    // a.id is an int64 (bigint); Number() is lossy above MAX_SAFE_INTEGER.
    // Fall back to null (placeholder id in the optimistic update) rather than
    // stamp a corrupted db id. GitHub ids are ~1e9, so this branch is defensive.
    databaseId: a.id <= BigInt(Number.MAX_SAFE_INTEGER) ? Number(a.id) : null,
    login: a.displayLogin,
    name: null,
    avatarUrl: a.avatarUrl,
    __typename: 'User',
  }))
  const originalNodeIds = new Set(selectedAssignees.map(a => a.id))

  return (
    <AssigneePicker
      owner={owner}
      repo={repo}
      number={number}
      assignees={selectedAssignees}
      viewer={null}
      selectionVariant="multiple"
      title="Assign up to 10 people"
      onSelectionChange={next => {
        // Diff on relay node id (the id space the picker and the mutation share).
        const humansNext = next.filter(a => !a.isCopilot && !a.isAgent)
        const nextNodeIds = new Set(humansNext.map(a => a.id))

        const addAssignees = humansNext.filter(a => !originalNodeIds.has(a.id)).map(a => a.id)
        const removeAssignees = selectedAssignees.filter(a => !nextNodeIds.has(a.id)).map(a => a.id)

        if (addAssignees.length === 0 && removeAssignees.length === 0) return
        mutate({addAssignees, removeAssignees, nextAssignees: humansNext})
      }}
      anchorElement={(anchorProps, ref) => <AssigneesHeader ref={ref} aria-haspopup="dialog" {...anchorProps} />}
    />
  )
}
